import { useState, useEffect, useCallback } from "react";
import {
  Star, Search, X, Send, Trophy, FileText, Package,
  CheckCircle, Award, TrendingDown, ChevronRight,
  AlertCircle, Loader2, FolderOpen
} from "lucide-react";
import API_BASE_URL from "../config";

// ─── Helpers ──────────────────────────────────────────────────
const fmt = (v) =>
  isNaN(v) || v === "" || v === null
    ? "—"
    : `₱${Number(v).toLocaleString("en-PH", { minimumFractionDigits: 2 })}`;

function StarRating({ value = 0, onChange, readOnly = false, size = 20 }) {
  const [hovered, setHovered] = useState(0);
  return (
    <div className="flex gap-0.5">
      {[1, 2, 3, 4, 5].map((s) => (
        <button key={s} type="button" disabled={readOnly}
          onClick={() => !readOnly && onChange?.(s)}
          onMouseEnter={() => !readOnly && setHovered(s)}
          onMouseLeave={() => !readOnly && setHovered(0)}
          className={`transition-transform ${readOnly ? "cursor-default" : "cursor-pointer hover:scale-125"}`}>
          <Star size={size}
            className={`transition-colors ${s <= (hovered || value) ? "fill-amber-400 text-amber-400" : "fill-slate-100 text-slate-300"}`} />
        </button>
      ))}
    </div>
  );
}

function Badge({ children, color = "blue" }) {
  const cls = {
    blue: "bg-blue-50 text-blue-700 border-blue-200",
    green: "bg-green-50 text-green-700 border-green-200",
    amber: "bg-amber-50 text-amber-700 border-amber-200",
    red: "bg-red-50 text-red-700 border-red-200",
    purple: "bg-purple-50 text-purple-700 border-purple-200",
    slate: "bg-slate-100 text-slate-600 border-slate-200",
  };
  return (
    <span className={`px-2 py-0.5 rounded-full text-xs font-semibold border ${cls[color]}`}>
      {children}
    </span>
  );
}

function StatusBadge({ status }) {
  const map = {
    0: { label: "Draft", color: "slate" },
    1: { label: "Submitted", color: "blue" },
    2: { label: "Awarded", color: "green" },
    3: { label: "Rejected", color: "red" },
  };
  const s = map[status] || map[0];
  return <Badge color={s.color}>{s.label}</Badge>;
}

// ─── Modal ────────────────────────────────────────────────────
function Modal({ title, icon, onClose, children, maxW = "max-w-2xl" }) {
  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className={`bg-white rounded-2xl shadow-2xl w-full ${maxW} border border-slate-200`}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100 bg-gradient-to-r from-blue-600 to-blue-700 rounded-t-2xl">
          <h3 className="text-white font-bold text-base flex items-center gap-2">{icon}{title}</h3>
          <button onClick={onClose} className="text-white/70 hover:text-white hover:bg-white/20 rounded-lg p-1.5 transition-all"><X size={18} /></button>
        </div>
        <div className="p-6">{children}</div>
      </div>
    </div>
  );
}

// ─── Main App ─────────────────────────────────────────────────
export default function QuotationApp() {
  const [page, setPage] = useState("quotation");



  // Add this state sa QuotationPage
  const [quotationCounts, setQuotationCounts] = useState({}); // { prHeaderId: count }

  // Add this useEffect after the existing ones
  useEffect(() => {
    fetch(`${API_BASE_URL}api/Quotation`)
      .then(r => r.json())
      .then(data => {
        const arr = Array.isArray(data) ? data : [];
        // Count per prHeaderId
        const counts = {};
        arr.forEach(q => {
          const id = q.prHeaderId || q.PRHeaderId;
          if (id) counts[id] = (counts[id] || 0) + 1;
        });
        setQuotationCounts(counts);
      })
      .catch(() => { });
  }, []);
  return (
    <div className="min-h-screen bg-slate-50">
      {/* NAV */}
      <nav className="bg-white border-b border-slate-200 px-6 py-3 flex items-center justify-between sticky top-0 z-40 shadow-sm">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-500 to-blue-700 flex items-center justify-center shadow">
            <FileText size={15} className="text-white" />
          </div>
          <span className="text-slate-800 font-bold text-base tracking-tight">Procurement Suite</span>
        </div>
        <div className="flex gap-1">
          <button onClick={() => setPage("quotation")}
            className={`px-4 py-2 rounded-xl text-sm font-semibold transition-all ${page === "quotation" ? "bg-blue-600 text-white shadow-md shadow-blue-200" : "text-slate-500 hover:text-blue-600 hover:bg-blue-50"}`}>
            📋 Quotation Form
          </button>
          <button onClick={() => setPage("top3")}
            className={`px-4 py-2 rounded-xl text-sm font-semibold transition-all ${page === "top3" ? "bg-amber-500 text-white shadow-md shadow-amber-200" : "text-slate-500 hover:text-amber-600 hover:bg-amber-50"}`}>
            🏆 Top 3 Best Prices
          </button>
        </div>
      </nav>

      {page === "quotation" ? <QuotationPage /> : <Top3Page />}
    </div>
  );
}

// ─── QUOTATION PAGE ───────────────────────────────────────────
function QuotationPage() {
  const [step, setStep] = useState(1);
  const [selectedPR, setSelectedPR] = useState(null);
  const [showPRModal, setShowPRModal] = useState(false);
  const [showSupplierModal, setShowSupplierModal] = useState(false);
  const [prList, setPRList] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [prSearch, setPrSearch] = useState("");
  const [supplierSearch, setSupplierSearch] = useState("");
  const [quoteNo, setQuoteNo] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loadingItems, setLoadingItems] = useState(false);
  const [error, setError] = useState("");

  const [form, setForm] = useState({
    supplier: null,
    validUntil: "",
    paymentTerms: "",
    deliveryDays: "",
    deliveryTerms: "",
    supplierRating: 0,
    remarks: "",
  });
  const [itemPrices, setItemPrices] = useState([]);

  // Fetch PR list
  useEffect(() => {
    fetch(`${API_BASE_URL}api/PR_Header`)
      .then(r => r.json())
      .then(data => setPRList(Array.isArray(data) ? data : []))
      .catch(() => setPRList([]));
  }, []);

  // ✅ FIX: Fetch Vendors (was api/Supplier)
  useEffect(() => {
    fetch(`${API_BASE_URL}api/Vendor`)
      .then(r => r.json())
      .then(data => setVendors(Array.isArray(data) ? data : []))
      .catch(() => setVendors([]));
  }, []);

  // Generate Quote No
  useEffect(() => {
    fetch(`${API_BASE_URL}api/Quotation/GenerateQuotationNo`)
      .then(r => r.text())
      .then(no => setQuoteNo(no.replace(/"/g, "")))
      .catch(() => setQuoteNo("QT-AUTO"));
  }, []);

  // ✅ FIX: Fetch PR_Details separately by prHeaderId
  const selectPR = async (pr) => {
    if (pr.status !== 2 && pr.Status !== 2) {
      setError("This PR is not yet approved.");
      return;
    }

    setSelectedPR(pr);
    setShowPRModal(false);
    setLoadingItems(true);
    setItemPrices([]);
    setError("");

    try {
      const res = await fetch(`${API_BASE_URL}api/PR_Details?prHeaderId=${pr.id}`);
      const details = await res.json();
      const items = Array.isArray(details) ? details : [];

      // ✅ Filter lang yung exact na prHeaderId — safety net kung bumabalik lahat ng items ang API
      const filtered = items.filter(item =>
        (item.prHeaderId || item.PRHeaderId) === pr.id
      );
      setItemPrices(filtered.map(item => ({
        prDetailId: item.id,
        itemCode: item.itemCode || "",
        itemDescription: item.itemDescription || "",
        specification: item.materialFinish || "",
        qty: item.qty || 0,
        uom: item.uom || "",
        unitPrice: "",
        discountPercent: "",
        discountAmount: 0,
        totalAmount: "",
        remarks: item.remarks || "",
        // ✅ DAGDAG
        warehouseCode: item.warehouseCode || item.warehouse || "—",
        isLocal: item.isLocal ?? item.local ?? null, // true=Local, false=Import
      })));
    } catch {
      setItemPrices([]);
      setError("Failed to load PR items. Please try again.");
    } finally {
      setLoadingItems(false);
      setStep(2);
    }
  };
  const updatePrice = (idx, field, value) => {
    setItemPrices(prev => {
      const updated = [...prev];
      updated[idx] = { ...updated[idx], [field]: value };
      const qty = parseFloat(updated[idx].qty) || 0;
      const price = parseFloat(field === "unitPrice" ? value : updated[idx].unitPrice) || 0;
      const disc = parseFloat(field === "discountPercent" ? value : updated[idx].discountPercent) || 0;
      const discAmt = qty * price * (disc / 100);
      updated[idx].discountAmount = discAmt.toFixed(2);
      updated[idx].totalAmount = (qty * price - discAmt).toFixed(2);
      return updated;
    });
  };

  const grandTotal = itemPrices.reduce((s, i) => s + (parseFloat(i.totalAmount) || 0), 0);

  const handleSubmit = async () => {
    setError("");
    if (!form.supplier) { setError("Please select a supplier."); return; }
    if (!form.validUntil) { setError("Please set Valid Until date."); return; }
    if (itemPrices.some(i => !i.unitPrice || parseFloat(i.unitPrice) <= 0)) {
      setError("Please fill in all unit prices."); return;
    }

    setIsSubmitting(true);
    try {
      const user = JSON.parse(localStorage.getItem("user") || "{}");
      const payload = {
        prHeaderId: selectedPR.id,
        // ✅ FIX: Map Vendor fields correctly
        supplierId: form.supplier.id,
        supplierName: form.supplier.VendorName || "",
        supplierAddress: form.supplier.Address1 || "",
        supplierContact: form.supplier.ContactNo || "",
        supplierEmail: form.supplier.Email || "",
        quotationDate: new Date().toISOString(),
        validUntil: new Date(form.validUntil).toISOString(),
        paymentTerms: form.paymentTerms,
        deliveryDays: parseInt(form.deliveryDays) || null,
        deliveryTerms: form.deliveryTerms,
        supplierRating: form.supplierRating,
        remarks: form.remarks,
        createdBy: user?.userId || 1,
        items: itemPrices.map((item, i) => ({
          prDetailId: item.prDetailId || null,
          itemCode: item.itemCode,
          itemDescription: item.itemDescription,
          specification: item.specification,
          qty: parseFloat(item.qty),
          uom: item.uom,
          unitPrice: parseFloat(item.unitPrice),
          discountPercent: parseFloat(item.discountPercent) || 0,
          discountAmount: parseFloat(item.discountAmount) || 0,
          totalAmount: parseFloat(item.totalAmount) || 0,
          remarks: item.remarks || "",
          lineNo: i + 1,
        }))
      };

      const res = await fetch(`${API_BASE_URL}api/Quotation`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload)
      });

      if (!res.ok) {
        const txt = await res.text();
        throw new Error(txt || "Submission failed.");
      }

      alert(`✅ Quotation ${quoteNo} submitted successfully!`);
      setStep(1);
      setSelectedPR(null);
      setForm({ supplier: null, validUntil: "", paymentTerms: "", deliveryDays: "", deliveryTerms: "", supplierRating: 0, remarks: "" });
      setItemPrices([]);

      // Regenerate quote number
      fetch(`${API_BASE_URL}api/Quotation/GenerateQuotationNo`)
        .then(r => r.text())
        .then(no => setQuoteNo(no.replace(/"/g, "")))
        .catch(() => { });
    } catch (err) {
      setError(err.message || "Failed to submit quotation.");
    } finally {
      setIsSubmitting(false);
    }
  };

  // ✅ Bagong state
  const [prDateRange, setPrDateRange] = useState("all");
  const [supplierPage, setSupplierPage] = useState(1);
  // ✅ Updated filteredPRs — approved only, date filtered, newest first
  const filteredPRs = prList
    .filter(pr => {
      const matchesSearch =
        pr.prNo?.toLowerCase().includes(prSearch.toLowerCase()) ||
        pr.requestor?.toLowerCase().includes(prSearch.toLowerCase());

      const isApproved = pr.status === 2 || pr.Status === 2;

      const matchesDate = (() => {
        if (prDateRange === "all") return true;
        const d = new Date(pr.createdAt || pr.dateNeeded);
        const now = new Date();
        const startOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        if (prDateRange === "today") return d >= startOfDay;
        if (prDateRange === "week") {
          const w = new Date(startOfDay);
          w.setDate(startOfDay.getDate() - startOfDay.getDay());
          return d >= w;
        }
        if (prDateRange === "month") {
          return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
        }
        return true;
      })();

      return matchesSearch && isApproved && matchesDate;
    })
    // ✅ Newest first
    .sort((a, b) => new Date(b.createdAt || b.dateNeeded) - new Date(a.createdAt || a.dateNeeded));

  // ✅ FIX: Filter by VendorName and VendorCode
  const filteredVendors = vendors.filter(s =>
    (s.VendorName || "").toLowerCase().includes(supplierSearch.toLowerCase()) ||
    (s.VendorCode || "").toLowerCase().includes(supplierSearch.toLowerCase())
  );
  // 1. Bagong state
  const [quotationCounts, setQuotationCounts] = useState({});
  const [prPage, setPrPage] = useState(1);
  // 2. Bagong useEffect (kasama ng ibang useEffects)
  useEffect(() => {
    fetch(`${API_BASE_URL}api/Quotation`)
      .then(r => r.json())
      .then(data => {
        const arr = Array.isArray(data) ? data : [];
        const counts = {};
        arr.forEach(q => {
          const id = q.prHeaderId || q.PRHeaderId;
          if (id) counts[id] = (counts[id] || 0) + 1;
        });
        setQuotationCounts(counts);
      })
      .catch(() => { });
  }, []);

  return (
    <div className="max-w-8xl mx-auto px-6 py-8">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-700 rounded-2xl shadow-xl p-7 mb-7">
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-3xl font-bold text-white">Quotation Form</h1>
            <p className="text-blue-100 text-sm mt-1">Connect to a PR, input supplier prices, then submit.</p>
          </div>
          <div className="bg-white/20 backdrop-blur px-5 py-2.5 rounded-xl text-white font-mono font-bold text-sm border border-white/30">
            {quoteNo || "Generating..."}
          </div>
        </div>
      </div>

      {/* Step Indicator */}
      <div className="flex items-center gap-3 mb-7">
        {["Select PR", "Fill Quotation"].map((label, i) => (
          <div key={i} className="flex items-center gap-2">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold border-2 transition-all ${step > i + 1 ? "bg-green-500 border-green-500 text-white" : step === i + 1 ? "bg-blue-600 border-blue-600 text-white" : "bg-white border-slate-300 text-slate-400"}`}>
              {step > i + 1 ? <CheckCircle size={15} /> : i + 1}
            </div>
            <span className={`text-sm font-semibold ${step >= i + 1 ? "text-slate-700" : "text-slate-400"}`}>{label}</span>
            {i < 1 && <ChevronRight size={16} className="text-slate-300 mx-1" />}
          </div>
        ))}
      </div>

      {/* STEP 1 */}
      {step === 1 && (
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-8">
          <h2 className="text-slate-800 font-bold text-xl mb-6 flex items-center gap-2">
            <Package size={20} className="text-blue-500" /> Select a Purchase Request
          </h2>
          <button onClick={() => setShowPRModal(true)}
            className="w-full py-6 border-2 border-dashed border-blue-300 rounded-xl text-blue-600 hover:border-blue-500 hover:bg-blue-50 transition-all flex items-center justify-center gap-3 font-semibold text-base">
            <Search size={20} /> Click to Search & Select PR
          </button>
        </div>
      )}

      {/* STEP 2 */}
      {step === 2 && selectedPR && (
        <div className="space-y-5">
          {/* PR Banner */}
          <div className="bg-blue-50 border border-blue-200 rounded-xl px-5 py-3.5 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 bg-blue-600 rounded-lg flex items-center justify-center"><FileText size={16} className="text-white" /></div>
              <div>
                <p className="text-slate-800 font-bold text-sm">{selectedPR.prNo}</p>
                <p className="text-slate-500 text-xs">{selectedPR.requestor} • {selectedPR.department} • Needed: {selectedPR.dateNeeded?.split("T")[0]}</p>
              </div>
            </div>
            <button onClick={() => { setStep(1); setSelectedPR(null); setItemPrices([]); }} className="text-blue-600 hover:text-blue-800 text-xs flex items-center gap-1 font-semibold">
              <X size={13} /> Change
            </button>
          </div>

          {/* Quotation Header */}
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6">
            <h3 className="text-slate-800 font-bold text-base mb-5 flex items-center gap-2">
              <FileText size={17} className="text-blue-500" /> Quotation Details
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 text-sm">
              {/* Quote No */}
              <div>
                <label className="block text-slate-500 text-xs font-semibold uppercase tracking-wider mb-1.5">Quote No.</label>
                <input className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3.5 py-2.5 text-slate-500 font-mono text-sm" value={quoteNo} readOnly />
              </div>
              {/* Date */}
              <div>
                <label className="block text-slate-500 text-xs font-semibold uppercase tracking-wider mb-1.5">Quote Date</label>
                <input className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3.5 py-2.5 text-slate-500 text-sm" value={new Date().toISOString().split("T")[0]} readOnly />
              </div>
              {/* Valid Until */}
              <div>
                <label className="block text-slate-500 text-xs font-semibold uppercase tracking-wider mb-1.5">Valid Until <span className="text-red-500">*</span></label>
                <input type="date" className="w-full border border-slate-200 rounded-lg px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={form.validUntil} onChange={e => setForm(p => ({ ...p, validUntil: e.target.value }))} />
              </div>
              {/* Vendor */}
              <div>
                <label className="block text-slate-500 text-xs font-semibold uppercase tracking-wider mb-1.5">Supplier / Vendor <span className="text-red-500">*</span></label>
                <div className="relative">
                  <input className="w-full border border-slate-200 rounded-lg px-3.5 py-2.5 pr-10 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 cursor-pointer"
                    value={form.supplier?.VendorName || ""}
                    placeholder="Click to search vendor..." readOnly onClick={() => setShowSupplierModal(true)} />
                  <button onClick={() => setShowSupplierModal(true)} className="absolute right-3 top-1/2 -translate-y-1/2 text-blue-500"><Search size={15} /></button>
                </div>
                {form.supplier && (
                  <p className="text-slate-400 text-xs mt-1">{form.supplier.VendorCode} • {form.supplier.Address1 || "No address"}</p>
                )}
              </div>
              {/* Payment Terms */}
              <div>
                <label className="block text-slate-500 text-xs font-semibold uppercase tracking-wider mb-1.5">
                  Payment Terms
                </label>

                <select
                  className="w-full border border-slate-300 rounded-lg px-3.5 py-2.5 text-sm bg-white shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                  value={form.paymentTerms}
                  onChange={(e) =>
                    setForm((p) => ({
                      ...p,
                      paymentTerms: e.target.value,
                    }))
                  }
                >
                  <option value="">-- Select Payment Terms --</option>

                  {/* Cash Basis */}
                  <option value="CASH">Cash Basis</option>
                  <option value="COD">COD</option>
                  <option value="PDC">PDC</option>

                  {/* Days */}
                  <option value="7_DAYS">7 Days</option>
                  <option value="15_DAYS">15 Days</option>
                  <option value="20_DAYS">20 Days</option>
                  <option value="21_DAYS">21 Days</option>
                  <option value="30_DAYS">30 Days</option>
                  <option value="45_DAYS">45 Days</option>
                  <option value="60_DAYS">60 Days</option>
                  <option value="90_DAYS">90 Days</option>
                  <option value="120_DAYS">120 Days</option>

                  {/* Others */}
                  <option value="CONS_90">CONS 90 Days</option>

                  <option value="DEFINE_NEW">Define New</option>
                </select>
              </div>
              {/* Delivery Days */}
              <div>
                <label className="block text-slate-500 text-xs font-semibold uppercase tracking-wider mb-1.5">Delivery Days</label>
                <input type="number" className="w-full border border-slate-200 rounded-lg px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="e.g. 7" value={form.deliveryDays} onChange={e => setForm(p => ({ ...p, deliveryDays: e.target.value }))} />
              </div>
            </div>

            {/* Rating */}
            <div className="mt-4 flex items-center gap-4 flex-wrap">
              <label className="text-slate-500 text-xs font-semibold uppercase tracking-wider">Supplier Rating</label>
              <StarRating value={form.supplierRating} onChange={v => setForm(p => ({ ...p, supplierRating: v }))} />
              {form.supplierRating > 0 && (
                <span className="text-amber-600 text-xs font-semibold">{["", "Poor", "Fair", "Good", "Very Good", "Excellent"][form.supplierRating]}</span>
              )}
              {/* Warehouse + Local/Import from PR items */}
              {itemPrices.length > 0 && (
                <div className="flex items-center gap-2 ml-2 border-l border-slate-200 pl-4">

                  {selectedPR && (
                    <div className="flex items-center gap-2 ml-2 border-l border-slate-200 pl-4">
                      {(selectedPR.Warehouse || selectedPR.warehouse) ? (
                        <span className="bg-slate-100 text-slate-600 border border-slate-200 text-xs px-2 py-0.5 rounded font-mono">
                          WH-{selectedPR.Warehouse || selectedPR.warehouse}
                        </span>
                      ) : (
                        <span className="bg-slate-100 text-slate-400 border border-slate-200 text-xs px-2 py-0.5 rounded">
                          WH-No WH-
                        </span>
                      )}
                      {(selectedPR.TypeOfPurchase || selectedPR.typeOfPurchase) && (
                        <span className="bg-purple-50 text-purple-700 border border-purple-200 text-xs px-2 py-0.5 rounded font-bold">
                          🏷️ {selectedPR.TypeOfPurchase || selectedPR.typeOfPurchase}
                        </span>
                      )}
                      {(selectedPR.Type || selectedPR.type) && (
                        <span className="bg-amber-50 text-amber-700 border border-amber-200 text-xs px-2 py-0.5 rounded font-bold">
                          ⚡ {selectedPR.Type || selectedPR.type}
                        </span>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>



            <div className="mt-4">
              <label className="block text-slate-500 text-xs font-semibold uppercase tracking-wider mb-1.5">Remarks</label>
              <textarea className="w-full border border-slate-200 rounded-lg px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none h-20"
                placeholder="Additional notes..." value={form.remarks} onChange={e => setForm(p => ({ ...p, remarks: e.target.value }))} />
            </div>
          </div>

          {/* Items Table */}
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6">
            <h3 className="text-slate-800 font-bold text-base mb-5 flex items-center gap-2">
              <Package size={17} className="text-blue-500" /> Item Prices
              {!loadingItems && itemPrices.length > 0 && <Badge color="blue">{itemPrices.length} items</Badge>}
            </h3>

            {/* Loading Items */}
            {loadingItems && (
              <div className="flex items-center justify-center py-12 text-slate-400">
                <Loader2 size={20} className="animate-spin mr-2" /> Loading PR items...
              </div>
            )}

            {/* No Items */}
            {!loadingItems && itemPrices.length === 0 && (
              <div className="text-center py-10 text-slate-400">
                <Package size={32} className="mx-auto mb-2 opacity-30" />
                <p className="text-sm">No items found for this PR</p>
              </div>
            )}

            {/* Desktop Table */}
            {!loadingItems && itemPrices.length > 0 && (
              <>
                <div className="hidden lg:block overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-slate-50 rounded-lg">
                        {["#", "Item Code", "Description", "Spec", "Qty", "UOM", "Unit Price (₱)", "Disc %", "Total"].map(h => (
                          <th key={h} className="text-left text-slate-500 text-xs font-semibold uppercase tracking-wider py-3 px-3 first:rounded-l-lg last:rounded-r-lg">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {itemPrices.map((item, idx) => (
                        <tr key={idx} className="border-b border-slate-100 hover:bg-slate-50 transition-colors">
                          <td className="py-3 px-3 text-slate-400 text-xs font-mono">{idx + 1}</td>
                          <td className="py-3 px-3">
                            <span className="bg-blue-50 text-blue-700 border border-blue-200 px-2 py-0.5 rounded text-xs font-mono">{item.itemCode || "—"}</span>
                          </td>
                          <td className="py-3 px-3 text-slate-700 font-medium text-sm">{item.itemDescription}</td>
                          <td className="py-3 px-3 text-slate-500 text-xs max-w-32 truncate" title={item.specification}>{item.specification || "—"}</td>
                          <td className="py-3 px-3 text-slate-600 text-sm font-semibold">{Number(item.qty).toLocaleString()}</td>
                          <td className="py-3 px-3"><Badge color="slate">{item.uom}</Badge></td>
                          <td className="py-3 px-3">
                            <input type="number" placeholder="0.00" value={item.unitPrice}
                              onChange={e => updatePrice(idx, "unitPrice", e.target.value)}
                              className="w-28 border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
                          </td>
                          <td className="py-3 px-3">
                            <input type="number" placeholder="0" value={item.discountPercent}
                              onChange={e => updatePrice(idx, "discountPercent", e.target.value)}
                              className="w-20 border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
                          </td>
                          <td className="py-3 px-3">
                            <span className={`font-bold text-sm ${item.totalAmount ? "text-green-600" : "text-slate-300"}`}>
                              {item.totalAmount ? fmt(item.totalAmount) : "—"}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="border-t-2 border-slate-200 bg-slate-50">
                        <td colSpan={8} className="py-3.5 px-3 text-right text-slate-600 font-bold text-sm">Grand Total:</td>
                        <td className="py-3.5 px-3"><span className="text-green-600 font-black text-lg">{fmt(grandTotal)}</span></td>
                      </tr>
                    </tfoot>
                  </table>
                </div>

                {/* Mobile Cards */}
                <div className="lg:hidden space-y-3">
                  {itemPrices.map((item, idx) => (
                    <div key={idx} className="border border-slate-200 rounded-xl p-4">
                      <div className="flex items-center justify-between mb-2">
                        <span className="bg-blue-50 text-blue-700 border border-blue-200 px-2 py-0.5 rounded text-xs font-mono">{item.itemCode || "—"}</span>
                        <Badge color="slate">{item.uom}</Badge>
                      </div>
                      <p className="text-slate-700 font-semibold text-sm mb-1">{item.itemDescription}</p>
                      {item.specification && <p className="text-slate-400 text-xs mb-3">{item.specification}</p>}
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <label className="text-slate-400 text-xs mb-1 block">Unit Price (₱)</label>
                          <input type="number" placeholder="0.00" value={item.unitPrice} onChange={e => updatePrice(idx, "unitPrice", e.target.value)}
                            className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                        </div>
                        <div>
                          <label className="text-slate-400 text-xs mb-1 block">Discount %</label>
                          <input type="number" placeholder="0" value={item.discountPercent} onChange={e => updatePrice(idx, "discountPercent", e.target.value)}
                            className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                        </div>
                      </div>
                      <div className="mt-2 flex justify-between items-center">
                        <span className="text-slate-400 text-xs">Qty: {Number(item.qty).toLocaleString()} {item.uom}</span>
                        <span className="text-green-600 font-bold text-sm">{item.totalAmount ? fmt(item.totalAmount) : "—"}</span>
                      </div>
                    </div>
                  ))}
                  <div className="border-2 border-green-200 bg-green-50 rounded-xl p-4 flex justify-between items-center">
                    <span className="text-slate-600 font-bold">Grand Total</span>
                    <span className="text-green-600 font-black text-xl">{fmt(grandTotal)}</span>
                  </div>
                </div>
              </>
            )}
          </div>

          {/* Error */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 flex items-center gap-2 text-red-700 text-sm">
              <AlertCircle size={16} /> {error}
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-end gap-3">
            <button onClick={() => { setStep(1); setSelectedPR(null); setItemPrices([]); }} className="px-5 py-2.5 rounded-xl bg-slate-100 text-slate-700 hover:bg-slate-200 font-semibold text-sm transition-all">← Back</button>
            <button onClick={handleSubmit} disabled={isSubmitting || loadingItems}
              className="px-7 py-2.5 rounded-xl bg-gradient-to-r from-green-500 to-emerald-600 text-white font-bold hover:from-green-400 hover:to-emerald-500 shadow-lg shadow-green-200 flex items-center gap-2 text-sm disabled:opacity-50 transition-all">
              {isSubmitting ? <><Loader2 size={15} className="animate-spin" /> Submitting...</> : <><Send size={15} /> Submit Quotation</>}
            </button>
          </div>
        </div>
      )}

      {/* PR Modal */}
      {showPRModal && (
        <Modal title="Select Purchase Request" icon={<FileText size={18} className="text-white" />} onClose={() => { setShowPRModal(false); setPrPage(1); }}>
          <div className="relative mb-3">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
            <input
              type="text"
              placeholder="Search PR No. or Requestor..."
              value={prSearch}
              onChange={e => { setPrSearch(e.target.value); setPrPage(1); }}
              className="w-full border border-slate-200 rounded-xl pl-9 pr-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              autoFocus
            />
          </div>

          {/* ✅ DATE RANGE FILTER */}
          <div className="flex gap-2 mb-3 flex-wrap">
            {[
              { key: "all", label: "All" },
              { key: "today", label: "Today" },
              { key: "week", label: "This Week" },
              { key: "month", label: "This Month" },
            ].map(({ key, label }) => (
              <button
                key={key}
                onClick={() => { setPrDateRange(key); setPrPage(1); }}
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all border ${prDateRange === key
                  ? "bg-blue-600 text-white border-blue-600"
                  : "bg-white text-slate-500 border-slate-200 hover:border-blue-300 hover:text-blue-600"
                  }`}
              >
                {label}
              </button>
            ))}
          </div>

          {(() => {
            const itemsPerPage = 6;
            const totalPages = Math.ceil(filteredPRs.length / itemsPerPage);
            const safePage = Math.min(prPage, totalPages || 1);
            const startIndex = (safePage - 1) * itemsPerPage;
            const paginated = filteredPRs.slice(startIndex, startIndex + itemsPerPage);

            return (
              <>
                {/* Results count */}
                <div className="flex justify-between items-center mb-2">
                  <p className="text-xs text-slate-400">
                    Showing <span className="font-bold text-blue-600">{filteredPRs.length === 0 ? 0 : startIndex + 1}–{Math.min(startIndex + itemsPerPage, filteredPRs.length)}</span> of <span className="font-bold text-slate-600">{filteredPRs.length}</span> PRs
                  </p>
                  <p className="text-xs text-slate-400">Page {safePage} of {totalPages || 1}</p>
                </div>

                {/* List */}
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {paginated.length === 0 ? (
                    <div className="text-center py-10 text-slate-400">
                      <FolderOpen size={32} className="mx-auto mb-2 opacity-40" />
                      <p className="text-sm">No PRs found</p>
                    </div>
                  ) : (
                    paginated.map(pr => (
                      <button
                        key={pr.id}
                        onClick={() => { selectPR(pr); setPrPage(1); }}
                        className="w-full text-left border border-slate-200 hover:border-blue-400 hover:bg-blue-50 rounded-xl p-4 transition-all"
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-slate-800 font-bold text-sm">{pr.prNo}</p>
                            <p className="text-slate-500 text-xs mt-0.5">{pr.requestor} • {pr.department}</p>
                          </div>
                          <div className="text-right flex flex-col items-end gap-1">
                            <p className="text-slate-400 text-xs">Needed: {pr.dateNeeded?.split("T")[0]}</p>
                            <div className="flex gap-1 mt-1">
                              {(pr.Warehouse || pr.warehouse) && (
                                <span className="bg-slate-100 text-slate-600 border border-slate-200 text-xs px-1.5 py-0.5 rounded font-mono">
                                  WH-{pr.Warehouse || pr.warehouse}
                                </span>
                              )}
                              {(pr.TypeOfPurchase || pr.typeOfPurchase) && (
                                <span className="bg-purple-50 text-purple-700 border border-purple-200 text-xs px-1.5 py-0.5 rounded font-bold">
                                  🏷️ {pr.TypeOfPurchase || pr.typeOfPurchase}
                                </span>
                              )}
                              {(pr.Type || pr.type) && (
                                <span className="bg-amber-50 text-amber-700 border border-amber-200 text-xs px-1.5 py-0.5 rounded font-bold">
                                   {pr.Type || pr.type}
                                </span>
                              )}
                            </div>
                            {quotationCounts[pr.id] > 0 ? (
                              <span className="inline-flex items-center gap-1 bg-blue-100 text-blue-700 border border-blue-200 text-xs font-bold px-2 py-0.5 rounded-full">
                                <FileText size={10} /> {quotationCounts[pr.id]} quote{quotationCounts[pr.id] !== 1 ? "s" : ""}
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1 bg-slate-100 text-slate-400 border border-slate-200 text-xs px-2 py-0.5 rounded-full">
                                No quotes yet
                              </span>
                            )}
                          </div>
                        </div>
                      </button>
                    ))
                  )}
                </div>

                {/* ✅ Pagination */}
                {totalPages > 1 && (
                  <div className="flex justify-center items-center gap-1 mt-3 pt-3 border-t border-slate-100 flex-wrap">
                    {/* First */}
                    <button
                      onClick={() => setPrPage(1)}
                      disabled={safePage === 1}
                      className="px-2 py-1 rounded-lg border border-slate-200 hover:bg-slate-100 disabled:opacity-40 text-xs font-semibold text-slate-600"
                    >
                      «
                    </button>
                    {/* Prev */}
                    <button
                      onClick={() => setPrPage(p => Math.max(1, p - 1))}
                      disabled={safePage === 1}
                      className="px-3 py-1 rounded-lg border border-slate-200 hover:bg-slate-100 disabled:opacity-40 text-xs font-semibold text-slate-600"
                    >
                      ‹ Prev
                    </button>

                    {/* Page numbers */}
                    {Array.from({ length: totalPages }, (_, i) => i + 1)
                      .filter(p => p === 1 || p === totalPages || Math.abs(p - safePage) <= 1)
                      .reduce((acc, p, idx, arr) => {
                        if (idx > 0 && p - arr[idx - 1] > 1) acc.push("...");
                        acc.push(p);
                        return acc;
                      }, [])
                      .map((p, idx) =>
                        p === "..." ? (
                          <span key={`dot-${idx}`} className="px-2 text-slate-400 text-xs">...</span>
                        ) : (
                          <button
                            key={p}
                            onClick={() => setPrPage(p)}
                            className={`px-3 py-1 rounded-lg border text-xs font-semibold transition-all ${safePage === p
                              ? "bg-blue-600 text-white border-blue-600"
                              : "border-slate-200 hover:bg-slate-100 text-slate-600"
                              }`}
                          >
                            {p}
                          </button>
                        )
                      )
                    }

                    {/* Next */}
                    <button
                      onClick={() => setPrPage(p => Math.min(totalPages, p + 1))}
                      disabled={safePage === totalPages}
                      className="px-3 py-1 rounded-lg border border-slate-200 hover:bg-slate-100 disabled:opacity-40 text-xs font-semibold text-slate-600"
                    >
                      Next ›
                    </button>
                    {/* Last */}
                    <button
                      onClick={() => setPrPage(totalPages)}
                      disabled={safePage === totalPages}
                      className="px-2 py-1 rounded-lg border border-slate-200 hover:bg-slate-100 disabled:opacity-40 text-xs font-semibold text-slate-600"
                    >
                      »
                    </button>
                  </div>
                )}
              </>
            );
          })()}
        </Modal>
      )}

      {/* Vendor Modal */}
      {showSupplierModal && (
        <Modal title="Select Vendor / Supplier" icon={<Package size={18} className="text-white" />} onClose={() => { setShowSupplierModal(false); setSupplierSearch(""); setSupplierPage(1); }}>
          <div className="relative mb-3">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
            <input
              type="text"
              placeholder="Search vendor name or code..."
              value={supplierSearch}
              onChange={e => { setSupplierSearch(e.target.value); setSupplierPage(1); }}
              className="w-full border border-slate-200 rounded-xl pl-9 pr-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              autoFocus
            />
          </div>

          {(() => {
            const itemsPerPage = 8;
            const totalPages = Math.ceil(filteredVendors.length / itemsPerPage);
            const safePage = Math.min(supplierPage, totalPages || 1);
            const startIndex = (safePage - 1) * itemsPerPage;
            const paginated = filteredVendors.slice(startIndex, startIndex + itemsPerPage);

            return (
              <>
                {/* Results count */}
                <div className="flex justify-between items-center mb-2">
                  <p className="text-xs text-slate-400">
                    Showing <span className="font-bold text-blue-600">{filteredVendors.length === 0 ? 0 : startIndex + 1}–{Math.min(startIndex + itemsPerPage, filteredVendors.length)}</span> of <span className="font-bold text-slate-600">{filteredVendors.length}</span> vendors
                  </p>
                  <p className="text-xs text-slate-400">Page {safePage} of {totalPages || 1}</p>
                </div>

                {/* List */}
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {paginated.length === 0 ? (
                    <div className="text-center py-10 text-slate-400">
                      <Package size={32} className="mx-auto mb-2 opacity-40" />
                      <p className="text-sm">No vendors found</p>
                    </div>
                  ) : (
                    paginated.map(s => (
                      <button
                        key={s.id}
                        onClick={() => {
                          setForm(p => ({ ...p, supplier: s }));
                          setShowSupplierModal(false);
                          setSupplierSearch("");
                          setSupplierPage(1);
                        }}
                        className="w-full text-left border border-slate-200 hover:border-blue-400 hover:bg-blue-50 rounded-xl px-4 py-3 transition-all"
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-slate-700 text-sm font-semibold">{s.VendorName}</p>
                            <p className="text-slate-400 text-xs mt-0.5">{s.VendorCode} • {s.Address1 || "No address"}</p>
                          </div>
                          <ChevronRight size={15} className="text-slate-400 flex-shrink-0" />
                        </div>
                      </button>
                    ))
                  )}
                </div>

                {/* ✅ Pagination */}
                {totalPages > 1 && (
                  <div className="flex justify-center items-center gap-1 mt-3 pt-3 border-t border-slate-100 flex-wrap">
                    {/* First */}
                    <button
                      onClick={() => setSupplierPage(1)}
                      disabled={safePage === 1}
                      className="px-2 py-1 rounded-lg border border-slate-200 hover:bg-slate-100 disabled:opacity-40 text-xs font-semibold text-slate-600"
                    >
                      «
                    </button>
                    {/* Prev */}
                    <button
                      onClick={() => setSupplierPage(p => Math.max(1, p - 1))}
                      disabled={safePage === 1}
                      className="px-3 py-1 rounded-lg border border-slate-200 hover:bg-slate-100 disabled:opacity-40 text-xs font-semibold text-slate-600"
                    >
                      ‹ Prev
                    </button>

                    {/* Page numbers */}
                    {Array.from({ length: totalPages }, (_, i) => i + 1)
                      .filter(p => p === 1 || p === totalPages || Math.abs(p - safePage) <= 1)
                      .reduce((acc, p, idx, arr) => {
                        if (idx > 0 && p - arr[idx - 1] > 1) acc.push("...");
                        acc.push(p);
                        return acc;
                      }, [])
                      .map((p, idx) =>
                        p === "..." ? (
                          <span key={`dot-${idx}`} className="px-2 text-slate-400 text-xs">...</span>
                        ) : (
                          <button
                            key={p}
                            onClick={() => setSupplierPage(p)}
                            className={`px-3 py-1 rounded-lg border text-xs font-semibold transition-all ${safePage === p
                              ? "bg-blue-600 text-white border-blue-600"
                              : "border-slate-200 hover:bg-slate-100 text-slate-600"
                              }`}
                          >
                            {p}
                          </button>
                        )
                      )
                    }

                    {/* Next */}
                    <button
                      onClick={() => setSupplierPage(p => Math.min(totalPages, p + 1))}
                      disabled={safePage === totalPages}
                      className="px-3 py-1 rounded-lg border border-slate-200 hover:bg-slate-100 disabled:opacity-40 text-xs font-semibold text-slate-600"
                    >
                      Next ›
                    </button>
                    {/* Last */}
                    <button
                      onClick={() => setSupplierPage(totalPages)}
                      disabled={safePage === totalPages}
                      className="px-2 py-1 rounded-lg border border-slate-200 hover:bg-slate-100 disabled:opacity-40 text-xs font-semibold text-slate-600"
                    >
                      »
                    </button>
                  </div>
                )}
              </>
            );
          })()}
        </Modal>
      )}
    </div>
  );
}

// ─── TOP 3 PAGE ───────────────────────────────────────────────
function Top3Page() {
  const [prList, setPRList] = useState([]);
  const [selectedPRId, setSelectedPRId] = useState("");
  const [top3, setTop3] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedWinner, setSelectedWinner] = useState(null);
  const [awarding, setAwarding] = useState(false);

  useEffect(() => {
    fetch(`${API_BASE_URL}api/PR_Header`)
      .then(r => r.json())
      .then(d => setPRList(Array.isArray(d) ? d : []))
      .catch(() => { });
  }, []);

  const loadTop3 = useCallback(async (prId) => {
    if (!prId) return;
    setLoading(true);
    setTop3([]);
    setSelectedWinner(null);
    try {
      const res = await fetch(`${API_BASE_URL}api/Quotation/Top3/${prId}`);
      const data = await res.json();
      setTop3(Array.isArray(data) ? data : []);
    } catch {
      setTop3([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadTop3(selectedPRId); }, [selectedPRId, loadTop3]);

  const handleAward = async () => {
    if (!selectedWinner) return;
    setAwarding(true);
    try {
      const user = JSON.parse(localStorage.getItem("user") || "{}");
      const res = await fetch(
        `${API_BASE_URL}api/Quotation/Award/${selectedWinner.quotationId}?awardedBy=${user?.userId || 1}`,
        { method: "PATCH" }
      );
      if (!res.ok) throw new Error("Award failed");
      const data = await res.json();
      alert(`🏆 ${data.message || "Quotation awarded successfully!"}`);
      loadTop3(selectedPRId);
    } catch (err) {
      alert("Failed to award: " + err.message);
    } finally {
      setAwarding(false);
    }
  };

  const medals = [
    { icon: "🥇", rank: "1st", cardBg: "bg-amber-50", border: "border-amber-300", rankColor: "text-amber-600", totalColor: "text-amber-600", btnGrad: "from-amber-400 to-yellow-500" },
    { icon: "🥈", rank: "2nd", cardBg: "bg-slate-50", border: "border-slate-300", rankColor: "text-slate-500", totalColor: "text-slate-700", btnGrad: "from-slate-400 to-slate-500" },
    { icon: "🥉", rank: "3rd", cardBg: "bg-orange-50", border: "border-orange-300", rankColor: "text-orange-600", totalColor: "text-orange-600", btnGrad: "from-orange-400 to-amber-600" },
  ];

  return (
    <div className="max-w-6xl mx-auto px-6 py-8">
      {/* Header */}
      <div className="bg-gradient-to-r from-amber-400 to-yellow-500 rounded-2xl shadow-xl p-7 mb-7">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-white flex items-center gap-3"><Trophy size={28} /> Top 3 Best Prices</h1>
            <p className="text-amber-100 text-sm mt-1">Compare quotations and award the best supplier.</p>
          </div>
          <select className="bg-white/90 border-0 rounded-xl px-4 py-2.5 text-slate-700 text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-white/50 cursor-pointer min-w-52 shadow"
            value={selectedPRId} onChange={e => setSelectedPRId(e.target.value)}>
            <option value="">— Select a PR —</option>
            {prList
              .filter(pr => pr.status === 2 || pr.Status === 2)  // ✅ Approved only
              .map(pr => <option key={pr.id} value={pr.id}>{pr.prNo} — {pr.requestor}</option>)
            }          </select>
        </div>
      </div>

      {loading && (
        <div className="flex items-center justify-center py-20 text-slate-500">
          <Loader2 size={24} className="animate-spin mr-3" /> Loading quotations...
        </div>
      )}

      {!selectedPRId && !loading && (
        <div className="bg-white rounded-2xl border border-slate-200 p-16 text-center">
          <Trophy size={48} className="mx-auto text-amber-300 mb-4" />
          <p className="text-slate-500 font-semibold text-lg">Select a PR to view Top 3 Quotations</p>
          <p className="text-slate-400 text-sm mt-1">Choose a purchase request from the dropdown above.</p>
        </div>
      )}

      {selectedPRId && !loading && top3.length === 0 && (
        <div className="bg-white rounded-2xl border border-slate-200 p-16 text-center">
          <FolderOpen size={48} className="mx-auto text-slate-200 mb-4" />
          <p className="text-slate-500 font-semibold">No quotations found for this PR</p>
          <p className="text-slate-400 text-sm mt-1">Submit quotations from the Quotation Form first.</p>
        </div>
      )}

      {!loading && top3.length > 0 && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-7">
            {top3.map((q, idx) => {
              const medal = medals[idx];
              const isWinner = selectedWinner?.quotationId === q.quotationId;
              return (
                <div key={q.quotationId}
                  onClick={() => setSelectedWinner(isWinner ? null : q)}
                  className={`relative rounded-2xl border-2 p-6 cursor-pointer transition-all ${isWinner ? `ring-2 ring-offset-2 ${medal.border} ${medal.cardBg} scale-105 shadow-xl` : `${medal.cardBg} ${medal.border} hover:shadow-lg`} ${idx === 0 ? "md:-mt-5" : ""}`}>
                  <div className={`absolute -top-5 left-1/2 -translate-x-1/2 w-11 h-11 rounded-full bg-gradient-to-br ${medal.btnGrad} flex items-center justify-center text-2xl shadow-lg`}>
                    {medal.icon}
                  </div>
                  {q.isAwarded && (
                    <div className="absolute top-3 right-3 bg-green-500 text-white text-xs font-bold px-2 py-0.5 rounded-full flex items-center gap-1">
                      <Award size={10} /> Awarded
                    </div>
                  )}
                  <div className="mt-5 text-center">
                    <p className={`text-xs font-bold uppercase tracking-widest ${medal.rankColor} mb-1`}>{medal.rank} Place</p>
                    <h3 className="text-slate-800 font-bold text-sm leading-tight mb-0.5">{q.supplierName}</h3>
                    <p className="text-slate-400 text-xs mb-4">{q.quotationNo}</p>
                    <div className={`rounded-xl px-4 py-3 mb-4 border ${idx === 0 ? "bg-amber-100 border-amber-200" : "bg-white border-slate-200"}`}>
                      <p className="text-slate-400 text-xs mb-0.5">Grand Total</p>
                      <p className={`font-black text-xl ${medal.totalColor}`}>{fmt(q.grandTotal)}</p>
                    </div>
                    <div className="flex flex-col items-center gap-1.5 mb-4">
                      <StarRating value={q.supplierRating} readOnly size={18} />
                      <span className="text-slate-400 text-xs">{["", "Poor", "Fair", "Good", "Very Good", "Excellent"][q.supplierRating] || "Unrated"}</span>
                    </div>
                    <div className="space-y-1.5 text-left border-t border-slate-100 pt-3">
                      {[
                        { label: "Payment", value: q.paymentTerms || "—" },
                        { label: "Delivery", value: q.deliveryDays ? `${q.deliveryDays} days` : "—" },
                        { label: "Valid Until", value: q.validUntil?.split("T")[0] || "—" },
                        { label: "Items", value: `${(q.items || []).length} line(s)` },
                      ].map(({ label, value }) => (
                        <div key={label} className="flex justify-between items-center text-xs">
                          <span className="text-slate-400">{label}</span>
                          <span className="text-slate-600 font-medium">{value}</span>
                        </div>
                      ))}
                    </div>
                    <button
                      onClick={e => { e.stopPropagation(); setSelectedWinner(isWinner ? null : q); }}
                      className={`mt-4 w-full py-2.5 rounded-xl text-xs font-bold transition-all ${isWinner ? `bg-gradient-to-r ${medal.btnGrad} text-white shadow` : "bg-white border border-slate-200 text-slate-600 hover:bg-slate-50"}`}>
                      {isWinner ? "✓ Selected as Winner" : "Select as Winner"}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>

          {selectedWinner && (
            <div className="bg-gradient-to-r from-amber-50 to-yellow-50 border-2 border-amber-300 rounded-2xl p-5 mb-7 flex flex-col md:flex-row items-center gap-4 shadow">
              <div className="text-4xl">🏆</div>
              <div className="flex-1 text-center md:text-left">
                <p className="text-amber-600 text-xs font-bold uppercase tracking-widest mb-0.5">Selected Supplier</p>
                <h2 className="text-slate-800 text-xl font-black">{selectedWinner.supplierName}</h2>
                <p className="text-slate-500 text-sm">{selectedWinner.quotationNo} • Grand Total: <span className="text-amber-600 font-bold">{fmt(selectedWinner.grandTotal)}</span></p>
              </div>
              <div className="flex items-center gap-3">
                <StarRating value={selectedWinner.supplierRating} readOnly />
                <button onClick={handleAward} disabled={awarding || selectedWinner.isAwarded}
                  className="px-6 py-2.5 bg-gradient-to-r from-amber-400 to-yellow-500 text-white rounded-xl font-bold text-sm hover:from-amber-300 hover:to-yellow-400 shadow flex items-center gap-2 disabled:opacity-60 transition-all">
                  {awarding ? <Loader2 size={14} className="animate-spin" /> : <Award size={14} />}
                  {selectedWinner.isAwarded ? "Already Awarded" : "Confirm Award"}
                </button>
              </div>
            </div>
          )}

          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6">
            <h3 className="text-slate-800 font-bold text-base mb-5 flex items-center gap-2">
              <TrendingDown size={17} className="text-blue-500" /> Price Comparison Table
            </h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-slate-50">
                    <th className="text-left text-slate-500 text-xs font-semibold uppercase tracking-wider py-3 px-3 rounded-l-lg">Item</th>
                    {top3.map((q, idx) => (
                      <th key={q.quotationId} className={`text-center text-xs font-semibold uppercase tracking-wider py-3 px-3 ${idx === 0 ? "text-amber-600" : "text-slate-400"}`}>
                        {medals[idx].icon} {q.supplierName.split(" ")[0]}
                      </th>
                    ))}
                    <th className="text-center text-green-600 text-xs font-semibold uppercase tracking-wider py-3 px-3 rounded-r-lg">Lowest</th>
                  </tr>
                </thead>
                <tbody>
                  {(top3[0]?.items || []).map((item, itemIdx) => {
                    const prices = top3.map(q => parseFloat(q.items?.[itemIdx]?.unitPrice) || null);
                    const minPrice = Math.min(...prices.filter(Boolean));
                    return (
                      <tr key={itemIdx} className="border-b border-slate-100 hover:bg-slate-50">
                        <td className="py-3 px-3">
                          <p className="text-slate-700 font-medium text-sm">{item.itemDescription}</p>
                          <p className="text-slate-400 text-xs">{item.itemCode} • {item.qty} {item.uom}</p>
                        </td>
                        {top3.map((q, qIdx) => {
                          const price = parseFloat(q.items?.[itemIdx]?.unitPrice) || null;
                          const isLowest = price === minPrice;
                          return (
                            <td key={q.quotationId} className="py-3 px-3 text-center">
                              <span className={`font-semibold text-sm ${isLowest ? "text-green-600" : "text-slate-600"}`}>{price ? fmt(price) : "—"}</span>
                              {isLowest && price && <span className="block text-green-600 text-xs font-bold">✓ Best</span>}
                            </td>
                          );
                        })}
                        <td className="py-3 px-3 text-center">
                          <span className="text-green-600 font-bold">{minPrice ? fmt(minPrice) : "—"}</span>
                        </td>
                      </tr>
                    );
                  })}
                  <tr className="border-t-2 border-slate-200 bg-slate-50">
                    <td className="py-4 px-3 text-slate-700 font-bold">Grand Total</td>
                    {top3.map((q, idx) => (
                      <td key={q.quotationId} className="py-4 px-3 text-center">
                        <span className={`font-black text-base ${idx === 0 ? "text-amber-600" : "text-slate-700"}`}>{fmt(q.grandTotal)}</span>
                      </td>
                    ))}
                    <td className="py-4 px-3 text-center">
                      <span className="text-amber-600 font-black text-base">{top3[0] ? fmt(top3[0].grandTotal) : "—"}</span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}